import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Reusable Error Boundary.
 *
 * Catches ANY runtime exception thrown during React render/commit inside
 * `children` and renders a friendly retry surface instead of a white page.
 *
 * Three variants:
 *   • variant="full" (default) — fullscreen fallback. Use at the app root.
 *   • variant="route"          — page-level fallback (padded card). Use per Route.
 *   • variant="section"        — compact inline fallback. Use around a single
 *                                dashboard widget so one bad widget cannot
 *                                blank the whole page.
 *
 * Motivating incident (10 Jul 2026): the Goods Return → Direct Dispatch save
 * flow crashed the entire app because a FastAPI 422 error payload
 * (`detail: [{...}]`) was passed straight to `sonner`'s `toast.error()`,
 * which tried to render an object as a React child and blew up outside the
 * component's own try/catch. Boundaries at every route/section guarantee a
 * bug like this can never white-screen the whole app again — the user always
 * sees a "Try again" button next to the affected surface only.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log for browser DevTools inspection (no third-party reporter installed).
    console.error(`[ErrorBoundary:${this.props.variant || "full"}]`, error, info?.componentStack);
    this.setState({ info });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, info: null });
  };

  handleFullReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const message = this.state.error?.message || String(this.state.error);
    const variant = this.props.variant || "full";
    const label = this.props.label; // optional section name

    // Compact inline fallback for a single widget/section
    if (variant === "section") {
      return (
        <div
          className="rounded-md border border-red-100 bg-red-50 p-3 text-sm"
          data-testid="error-boundary-section"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-red-800">
                {label ? `${label} failed to load` : "This section failed to load"}
              </div>
              {message && (
                <div className="mt-1 text-xs text-red-700 truncate" title={message}>
                  {message}
                </div>
              )}
              <button
                onClick={this.handleReset}
                className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-white border border-red-200 text-red-700 text-xs font-medium hover:bg-red-100"
                data-testid="error-boundary-section-retry"
              >
                <RefreshCw className="w-3 h-3" />
                Retry
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Route-level fallback: card centered in the current page area (not fullscreen)
    if (variant === "route") {
      return (
        <div
          className="p-6 flex items-start justify-center"
          data-testid="error-boundary-route"
        >
          <div className="max-w-lg w-full bg-white rounded-lg shadow border border-red-100 p-5">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-md bg-red-50 text-red-600 ring-1 ring-red-100 shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-semibold text-slate-900">
                  {label ? `${label} could not be displayed` : "This page could not be displayed"}
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  An unexpected error prevented this page from rendering. Your data is safe — nothing was lost. Other pages still work normally.
                </p>
                {message && (
                  <pre
                    className="mt-3 text-xs font-mono text-red-700 bg-red-50 border border-red-100 rounded p-2 max-h-32 overflow-auto whitespace-pre-wrap"
                    data-testid="error-boundary-route-message"
                  >
                    {message}
                  </pre>
                )}
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={this.handleReset}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
                    data-testid="error-boundary-route-retry"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Try Again
                  </button>
                  <button
                    onClick={this.handleFullReload}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
                    data-testid="error-boundary-route-reload"
                  >
                    Reload Page
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Fullscreen fallback (default) — used at the app root
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50 p-4"
        data-testid="error-boundary"
      >
        <div className="max-w-lg w-full bg-white rounded-lg shadow-lg border border-red-100 p-6">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-md bg-red-50 text-red-600 ring-1 ring-red-100 shrink-0">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-900">Something went wrong</h1>
              <p className="text-sm text-slate-600 mt-1">
                An unexpected error prevented this page from rendering. Your data is safe — nothing was lost.
              </p>
              {message && (
                <pre
                  className="mt-3 text-xs font-mono text-red-700 bg-red-50 border border-red-100 rounded p-2 max-h-40 overflow-auto whitespace-pre-wrap"
                  data-testid="error-boundary-message"
                >
                  {message}
                </pre>
              )}
              <div className="mt-4 flex gap-2">
                <button
                  onClick={this.handleReset}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
                  data-testid="error-boundary-retry"
                >
                  <RefreshCw className="w-4 h-4" />
                  Try Again
                </button>
                <button
                  onClick={this.handleFullReload}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
                  data-testid="error-boundary-reload"
                >
                  Reload Page
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
